import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Check, X, Eye, Loader2 } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { format } from 'date-fns';

interface PremiumRequest {
  id: string;
  assessment_id: string;
  full_name: string | null;
  email: string;
  company: string | null;
  designation: string | null;
  pdf_url: string | null;
  status: string;
  reviewed_at: string | null;
  created_at: string;
  assessment_title?: string;
  assessment_slug?: string;
}

const fetchPremiumRequests = async (): Promise<PremiumRequest[]> => {
  const { data, error } = await supabase
    .from('premium_access_requests')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) throw error;

  // Fetch assessment titles
  const assessmentIds = [...new Set((data || []).map(r => r.assessment_id))];
  const { data: assessments } = await supabase
    .from('assessments')
    .select('id, title, slug')
    .in('id', assessmentIds);

  const assessmentMap = new Map(assessments?.map(a => [a.id, a]) || []);

  return (data || []).map(r => ({
    ...r,
    assessment_title: assessmentMap.get(r.assessment_id)?.title || 'Unknown',
    assessment_slug: assessmentMap.get(r.assessment_id)?.slug || '',
  }));
};

export default function PremiumRequests() {
  const queryClient = useQueryClient();

  const { data: requests = [], isLoading } = useQuery({
    queryKey: ['premium-access-requests'],
    queryFn: fetchPremiumRequests,
  });

  const approveMutation = useMutation({
    mutationFn: async (request: PremiumRequest) => {
      // Update status
      const { error } = await supabase
        .from('premium_access_requests')
        .update({ status: 'approved', reviewed_at: new Date().toISOString() })
        .eq('id', request.id);
      if (error) throw error;

      // Get site URL for premium link
      const { data: settings } = await supabase
        .from('app_settings')
        .select('site_url')
        .limit(1)
        .single();

      const siteUrl = (settings?.site_url || 'https://maturity-compass.gapstars.net').replace(/\/+$/, '');
      const premiumLink = `${siteUrl}/assessment/${request.assessment_slug}/premium`;

      // Send premium link email
      const { error: fnError } = await supabase.functions.invoke('send-premium-link', {
        body: {
          email: request.email,
          fullName: request.full_name || '',
          assessmentTitle: request.assessment_title || '',
          premiumLink,
        },
      });
      if (fnError) throw fnError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['premium-access-requests'] });
      queryClient.invalidateQueries({ queryKey: ['premium-requests-count'] });
      toast({ title: 'Approved', description: 'Premium link sent to user.' });
    },
    onError: (err: any) => {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    },
  });

  const denyMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('premium_access_requests')
        .update({ status: 'denied', reviewed_at: new Date().toISOString() })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['premium-access-requests'] });
      queryClient.invalidateQueries({ queryKey: ['premium-requests-count'] });
      toast({ title: 'Denied', description: 'Request has been denied.' });
    },
    onError: (err: any) => {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    },
  });

  const statusBadge = (status: string) => {
    switch (status) {
      case 'pending': return <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">Pending</Badge>;
      case 'approved': return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Approved</Badge>;
      case 'denied': return <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">Denied</Badge>;
      default: return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Company</TableHead>
              <TableHead>Designation</TableHead>
              <TableHead>Assessment</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Requested</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {requests.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                  No premium access requests yet.
                </TableCell>
              </TableRow>
            ) : (
              requests.map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="font-medium">{r.full_name || '—'}</TableCell>
                  <TableCell>{r.email}</TableCell>
                  <TableCell>{r.company || '—'}</TableCell>
                  <TableCell>{r.designation || '—'}</TableCell>
                  <TableCell>{r.assessment_title}</TableCell>
                  <TableCell>{statusBadge(r.status)}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {format(new Date(r.created_at), 'MMM d, yyyy')}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      {r.pdf_url && (
                        <Button variant="ghost" size="icon" asChild>
                          <a href={r.pdf_url} target="_blank" rel="noopener noreferrer">
                            <Eye className="h-4 w-4" />
                          </a>
                        </Button>
                      )}
                      {r.status === 'pending' && (
                        <>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-green-600 hover:text-green-700 hover:bg-green-50"
                            onClick={() => approveMutation.mutate(r)}
                            disabled={approveMutation.isPending}
                          >
                            <Check className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => denyMutation.mutate(r.id)}
                            disabled={denyMutation.isPending}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
